import * as React from 'react';
import { View } from 'react-native';
import Animated, { withTiming, interpolate, Extrapolation, useSharedValue, useAnimatedProps, useAnimatedStyle, Reanimated3DefaultSpringConfig, withSpring, } from 'react-native-reanimated';
import Svg from 'react-native-svg';
import Text from 'design-system/Text';
import { rem } from 'design-system/values';
import { AnimatedPath } from 'design-system/AnimatedComponents';
import useColors from 'hooks/useColors';
import useStyles from 'hooks/useStyles';
import createStyleSheets from 'utils/createStyleSheets';
import { MAX_MIN_INDICATOR_VIEW_HEIGHT } from '../constants';
const MaxPriceIndicatorView = ({ maxPoint, width, height, selectedInfoObj }) => {
    const didMount = React.useRef(false);
    const styles = useStyles(styleSet);
    const colors = useColors();
    const x = useSharedValue(maxPoint.x);
    const y = useSharedValue(maxPoint.y);
    React.useEffect(() => {
        if (didMount.current) {
            x.value = withTiming(maxPoint.x);
            y.value = withTiming(maxPoint.y);
        }
        else {
            didMount.current = true;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [width, maxPoint]);
    const animatedLineProps = useAnimatedProps(() => {
        const props = {
            d: `M${x.value * width} 18 l0 8`,
        };
        return props;
    }, [x, width]);
    const leftViewAnimatedStyles = useAnimatedStyle(() => ({
        flex: interpolate(x.value, [0, 0.05, 0.25, 0.5, 0.75, 0.95, 1], [0, 0, 0.2, 0.5, 0.8, 1, 1], Extrapolation.CLAMP),
    }), [x]);
    const rightViewAnimatedStyles = useAnimatedStyle(() => ({
        flex: interpolate(x.value, [0, 0.05, 0.25, 0.5, 0.75, 0.95, 1], [1, 1, 0.8, 0.5, 0.2, 0, 0], Extrapolation.CLAMP),
    }), [x]);
    const containerAnimatedStyles = useAnimatedStyle(() => ({
        transform: [
            {
                translateY: y.value * height,
            },
        ],
        opacity: withSpring(!selectedInfoObj.value ? 1 : 0, Reanimated3DefaultSpringConfig),
    }), [height, selectedInfoObj, y]);
    return (<View pointerEvents="none" style={styles.container}>
      <Animated.View style={containerAnimatedStyles}>
        <Svg width={width} height={MAX_MIN_INDICATOR_VIEW_HEIGHT} viewBox={`0 0 ${width} ${MAX_MIN_INDICATOR_VIEW_HEIGHT}`}>
          <AnimatedPath strokeWidth={1} animatedProps={animatedLineProps} stroke={colors.elements.secondary}/>
        </Svg>
        <View style={styles.textContainer}>
          <Animated.View style={leftViewAnimatedStyles}/>
          <Text NumbersThin-12 Secondary>
            {maxPoint.formattedPrice}
          </Text>
          <Animated.View style={rightViewAnimatedStyles}/>
        </View>
      </Animated.View>
    </View>);
};
const MaxPriceIndicator = ({ maxPoint, width, height, selectedInfoObj }) => {
    const didMount = React.useRef(false);
    const [privateMaxPoint, setPrivateMaxPoint] = React.useState(maxPoint);
    React.useEffect(() => {
        if (didMount.current) {
            if (maxPoint !== undefined) {
                setPrivateMaxPoint(maxPoint);
            }
        }
        else {
            didMount.current = true;
        }
    }, [maxPoint]);
    if (!privateMaxPoint) {
        return null;
    }
    return (<MaxPriceIndicatorView width={width} height={height} maxPoint={privateMaxPoint} selectedInfoObj={selectedInfoObj}/>);
};
export default React.memo(MaxPriceIndicator);
const styleSet = createStyleSheets(() => ({
    container: {
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1,
        position: 'absolute',
        height: MAX_MIN_INDICATOR_VIEW_HEIGHT,
    },
    priceView: {
        left: rem(6),
        height: MAX_MIN_INDICATOR_VIEW_HEIGHT,
        borderRadius: rem(4),
        position: 'absolute',
        justifyContent: 'center',
        paddingHorizontal: rem(6),
    },
    textContainer: {
        top: 0,
        bottom: 0,
        left: rem(2),
        right: rem(2),
        position: 'absolute',
        flexDirection: 'row',
    },
}));
